/**
 * The "What's an AI Service?" explainer copy (task 605).
 *
 * Lifted verbatim out of the first-run Dashboard checklist
 * (`DashboardOnboarding`, task 525) so the Help menu's explainer dialog and the
 * checklist card render the same words — they can never disagree, because there
 * is only one copy of them. Same shape as `HowItWorksStrip` (task 526): the
 * component owns the prose and the text styling, each surface supplies its own
 * card or dialog chrome around it.
 *
 * `testId` keeps the checklist's original `dashboard-onboarding-explainer` hook
 * intact on the card wrapper; other surfaces get the default.
 */

/** The heading every surface titles this explainer with. */
export const WHATS_AN_AI_SERVICE_TITLE = "What's an AI Service?";

interface WhatIsAnAiServiceExplainerProps {
  /** Test hook for the copy block. Defaults to `whats-an-ai-service-explainer`. */
  testId?: string;
  /** Extra classes for the surface's own spacing. */
  className?: string;
}

export default function WhatIsAnAiServiceExplainer({
  testId = 'whats-an-ai-service-explainer',
  className = '',
}: WhatIsAnAiServiceExplainerProps = {}) {
  return (
    <div
      data-testid={testId}
      className={`space-y-2.5 text-sm text-gray-700 dark:text-gray-300 ${className}`}
    >
      <p>
        An AI Service is a piece of AI that does one job well — triage tickets, draft replies,
        summarize documents — and is callable by your apps the moment it's built.
      </p>
      <p>
        You create one by <span className="font-semibold">describing it in a chat</span>. The same
        chat stays attached to the service forever, so refining it later is just… continuing the
        conversation.
      </p>
      <p className="text-gray-500 dark:text-gray-400">
        No prompts to engineer, no models to pick — unless you want to. Everything the service is
        made of stays visible on its own page.
      </p>
    </div>
  );
}
